import {
   Actions,
   CollectionDescriptor,
   CollectionResponse,
   ReadOptions,
   Root,
   Storage
} from 'firestar'
import firebase from 'firebase/compat/app'
import {
   Members,
   Plugins
} from '.'

export const ChannelOptions = {
   'mobile': 'Mobile',
   'web': 'Web'
}

export const StatusOptions = {
   'draft': 'Draft',
   'published': 'Published',
   'completed': 'Completed',
}

type ChannelType = keyof typeof ChannelOptions
type StatusType = keyof typeof StatusOptions

export interface PromotionInfoType {
   id: string,
   name: string
}

interface PromotionType extends PromotionInfoType, Root {
   startDate: firebase.firestore.Timestamp | Date,
   endDate: firebase.firestore.Timestamp | Date,
   status: StatusType,
   productIds: string[],
   thumbnail?: string | null
   acceptVouchers?: boolean,
   allowMultipleClaims?: boolean,
   claimsPerMember?: number,
   validateLocation?: boolean,
   allowTiers?: boolean,
   limitClaims?: number
   totalClaims?: number,
   enableMemberByDefault?: boolean,
   allowShippingAddress?: boolean
   channels: ChannelType[],
   disableClaim?: boolean,
   sendEmail?: boolean,
   plugins: {[key: string] : Plugins.Params }
}

namespace Promotions {

   export type Promotion = PromotionType
   export type PromotionInfo = PromotionInfoType
   export type Status = StatusType
   export type Channel = ChannelType

   export function getPromotions(
      casinoId: string, descriptor?: CollectionDescriptor) {
      return Actions.readCollection<Promotion>(`casinos/${casinoId}/promotions`, descriptor)
   }

   export function getPromotionInfo(promotion: Promotion): PromotionInfo {
      return {
         id: promotion.id,
         name: promotion.name
      }
   }

   export function addPromotion(casinoId: string, document: Promotion) {
      return Actions.addToCollection<Promotion>(`casinos/${casinoId}/promotions`, document)
   }

   export function updatePromotion(
      casinoId: string,
      promotionId: string,
      document: Partial<Promotion> | Promotion) {
      return Actions.updateDocument<Promotion>(
         `casinos/${casinoId}/promotions/${promotionId}`, document
      )
   }

   export function uploadThumbnail(
      casinoId: string,
      promotionId: string,
      data: any) {

      const fileType = data.substring("data:image/".length, data.indexOf(";base64"))
      console.log('fileType', fileType)
      return Storage.uploadFileDataToPath(
         `casinos/${casinoId}/promotions/${promotionId}/thumbnail.${fileType ?? 'png'}`,
         data,
         'data_url',
         {
            contentType: 'image/ong'
         }
      )
   }

   export function getPromotion(
      casinoId: string,
      promotionId: string,
      readOptions?: ReadOptions
   ) {
      return Actions.readDocument<Promotion>(
         `casinos/${casinoId}/promotions/${promotionId}`
         , readOptions)
   }

   export function getPromotionWithId(
      promotionId: string,
   ) {
      return new Promise<undefined | Promotion>(async (resolve, reject) => {
         try {
            const response = await Actions.readCollectionGroup<Promotion>('promotions', {
               where: [
                  {
                     fieldPath: 'id',
                     opStr: '==',
                     value: promotionId
                  }
               ],
            })
            if (response.data.length == 1) {
               resolve(response.data[0])
            } else {
               resolve(undefined)
            }
         } catch (error) {
            reject(error)
         }
      })
   }

   export function deletePromotion(casinoId: string, promotionId: string) {
      return Actions.deleteDocument(
         `casinos/${casinoId}/promotions/${promotionId}`
      )
   }

   export function isPromotionActive(promotion: Promotion) {

      const startDate = promotion.startDate as firebase.firestore.Timestamp
      const endDate = promotion.endDate as firebase.firestore.Timestamp

      const startTime = startDate.toDate().getTime() //1
      const endTime = endDate.toDate().getTime() // 3
      const nowTime = new Date().getTime() // 2

      return nowTime >= startTime && nowTime <= endTime
   }

   export function getPublishedPromotionsForCasinos(
      casinoId: string,
      descriptor?: CollectionDescriptor,
      readOptions?: ReadOptions
   ) {
      return new Promise<CollectionResponse<Promotion>>(async (resolve, reject) => {
         try {
            const response = await Actions.readCollection<Promotion>(`casinos/${casinoId}/promotions`, {
               ...descriptor,
               where: [
                  ...descriptor?.where ?? [],
                  {
                     fieldPath: 'status',
                     opStr: '==',
                     value: 'published'
                  },
                  {
                     fieldPath: 'endDate',
                     opStr: '>=',
                     value: new Date()
                  }
               ],
               orderBy: {
                  fieldPath: 'endDate',
                  directionStr: 'asc'
               }
            }, readOptions)
            const promotions = response.data as Promotion[]
            // const filtered = promotions.filter((p) => {
            //    const startDate = p.startDate as firebase.firestore.Timestamp
            //    const startTime = startDate.toDate()
            //    const nowTime = new Date()
            //    return nowTime >= startTime
            // })
            resolve({
               data: promotions,
               lastDoc: response.lastDoc
            })
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getActivePromotionsForCasinos(
      casinoId: string,
      descriptor?: CollectionDescriptor,
      readOptions?: ReadOptions
   ) {
      return new Promise<CollectionResponse<Promotion>>(async (resolve, reject) => {
         try {
            const response = await Actions.readCollection<Promotion>
               (`casinos/${casinoId}/promotions`, {
                  ...descriptor,
                  where: [
                     ...descriptor?.where ?? [],
                     {
                        fieldPath: 'status',
                        opStr: '==',
                        value: 'published'
                     },
                     {
                        fieldPath: 'endDate',
                        opStr: '>=',
                        value: new Date()
                     }
                  ]
               }, readOptions)
            const promotions = response.data as Promotion[]
            const filtered = promotions.filter((p) => {
               const startDate = p.startDate as firebase.firestore.Timestamp
               const startTime = startDate.toDate()
               const nowTime = new Date()
               return nowTime >= startTime
            })
            resolve({
               data: filtered,
               lastDoc: response.lastDoc
            })
         } catch (error) {
            reject(error)
         }
      })
   }

   export function getPromotionsForCardNumber(
      casinoId: string,
      cardNumber: string,
   ) {
      return new Promise<CollectionResponse<Promotion>>(async (resolve, reject) => {
         try {

            console.log('Getting eligible promotions for card number', cardNumber)
            const promotionResponse = await getActivePromotionsForCasinos(casinoId, {
               limit: 100
            })
            const activePromotions = promotionResponse.data
            const lastDoc = promotionResponse.lastDoc

            const promises = activePromotions.map((promotion) => {
               return new Promise<Members.Member | null>(async (resolve, reject) => {
                  try {
                     const response = await Members.getAllMembersForPromotion(casinoId, promotion.id, {
                        where: [
                           {
                              fieldPath: 'cardNumber',
                              opStr: '==',
                              value: cardNumber
                           }
                        ]
                     })
                     if (response.data.length > 0) {
                        resolve(response.data[0])
                     } else {
                        resolve(null)
                     }
                  } catch (error) {
                     reject(error)
                  }
               })
            })

            const memberResponse = await Promise.all(promises)
            console.log('memberResponse', memberResponse)

            const eligiblePromotions = memberResponse.map((member, index) => {
               if (member != null) {
                  const promotion = activePromotions[index]
                  return promotion
               }
               return null
            }).filter((p) => p != null) as Promotion[]

            resolve({
               data: eligiblePromotions,
               lastDoc: lastDoc
            })

         } catch (error) {
            reject(error)
         }
      })
   }

}

export default Promotions